import api from './api';

export interface TraineeStats {
  enrolledCourses: number;
  completedCourses: number;
  certificates: number;
  totalHours: number;
  averageScore: number;
  upcomingSessions: number;
}

export interface RecentActivity {
  id: string;
  type: 'enrollment' | 'completion' | 'certificate' | 'session' | 'notification';
  title: string;
  description: string;
  date: string;
  status: 'completed' | 'in-progress' | 'upcoming' | 'confirmed';
}

export interface RecommendedCourse {
  id: string;
  title: string;
  instructor: string;
  rating: number;
  students: number;
  price: string;
  domaine: string;
  duration: string;
  level: string;
  location?: string;
  centerName?: string;
}

export interface DashboardData {
  stats: TraineeStats;
  recentActivity: RecentActivity[];
  recommendedCourses: RecommendedCourse[];
}

class DashboardService {
  async getTraineeDashboardData(): Promise<DashboardData> {
    try {
      const response = await api.get('/api/trainee/dashboard');
      return response.data;
    } catch (error) {

      // Return fallback data if API fails
      return this.getFallbackData();
    }
  }

  async getTraineeStats(): Promise<TraineeStats> {
    try {
      const response = await api.get('/api/trainee/stats');
      return response.data;
    } catch (error) {

      return this.getFallbackStats();
    }
  }

  async getRecentActivity(): Promise<RecentActivity[]> {
    try {
      const response = await api.get('/api/trainee/activity');
      return response.data;
    } catch (error) {

      return this.getFallbackActivity();
    }
  }

  async getRecommendedCourses(): Promise<RecommendedCourse[]> {
    try {
      const response = await api.get('/api/public/trainings/featured');

      if (Array.isArray(response.data)) {
        return response.data.map((training: any) => ({
          id: training.id,
          title: training.title,
          instructor: training.trainer || 'Instructeur PHINLEX',
          rating: training.rating || 0,
          students: training.views || 0,
          price: training.pricing_dzd ? `${training.pricing_dzd.toLocaleString()} DA` : 'Gratuit',
          domaine: training.domaine?.name || 'Formation',
          duration: training.duration || 'N/A',
          level: training.level || 'Tous niveaux',
          location: training.city || 'Alger',
          centerName: training.training_center?.name || 'Centre de formation'
        }));
      } else {

        return this.getFallbackCourses();
      }
    } catch (error) {

      return this.getFallbackCourses();
    }
  }

  private getFallbackData(): DashboardData {
    return {
      stats: this.getFallbackStats(),
      recentActivity: this.getFallbackActivity(),
      recommendedCourses: this.getFallbackCourses()
    };
  }

  private getFallbackStats(): TraineeStats {
    return {
      enrolledCourses: 0,
      completedCourses: 0,
      certificates: 0,
      totalHours: 0,
      averageScore: 0,
      upcomingSessions: 0
    };
  }

  private getFallbackActivity(): RecentActivity[] {
    return [
      {
        id: '1',
        type: 'notification',
        title: 'Bienvenue sur PHINLEX !',
        description: 'Commencez par rechercher et vous inscrire aux formations présentielles disponibles',
        date: new Date().toISOString(),
        status: 'upcoming'
      }
    ];
  }

  private getFallbackCourses(): RecommendedCourse[] {
    return [
      {
        id: '1',
        title: 'Pharmacologie Clinique',
        instructor: 'Dr. Ahmed Benali',
        rating: 4.8,
        students: 156,
        price: 'Gratuit',
        domaine: 'Pharmacologie',
        duration: '20h',
        level: 'Intermédiaire',
        location: 'Alger',
        centerName: 'Centre de Formation Pharmaceutique'
      },
      {
        id: '2',
        title: 'Gestion des Médicaments',
        instructor: 'Prof. Sarah Merabet',
        rating: 4.9,
        students: 203,
        price: '15,000 DA',
        domaine: 'Gestion',
        duration: '15h',
        level: 'Débutant',
        location: 'Oran',
        centerName: 'Institut de Formation Médicale'
      },
      {
        id: '3',
        title: 'Sécurité Pharmaceutique',
        instructor: 'Dr. Karim Boudiaf',
        rating: 4.7,
        students: 89,
        price: '12,000 DA',
        domaine: 'Sécurité',
        duration: '12h',
        level: 'Avancé',
        location: 'Constantine',
        centerName: 'Centre d\'Excellence Pharmaceutique'
      }
    ];
  }
}

export default new DashboardService();
